import React from 'react';
import { CheckCircle2, AlertCircle, Loader2, Sparkles, X } from 'lucide-react';
import { WorkspaceUser, BatchUpdateResponse } from '../types';

interface BatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUsers: WorkspaceUser[];
  progress: number;
  isProcessing: boolean;
  result: BatchUpdateResponse | null;
}

export const BatchModal: React.FC<BatchModalProps> = ({
  isOpen,
  onClose,
  selectedUsers,
  progress,
  isProcessing,
  result,
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-card">
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, rgba(2, 132, 199, 0.08) 0%, transparent 100%)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'rgba(2, 132, 199, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0066FF',
              }}
            >
              <Sparkles size={18} />
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0B192C', margin: 0 }}>
                Wstrzykiwanie stopek do kont Google Workspace
              </h3>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Batch API &bull; Gmail sendAs.update &bull; {selectedUsers.length} kont
              </div>
            </div>
          </div>

          {!isProcessing && (
            <button
              onClick={onClose}
              className="btn btn-outline"
              style={{ padding: '6px', borderRadius: 8 }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Progress Section */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <span style={{ color: '#0B192C', display: 'flex', alignItems: 'center', gap: 8 }}>
                {isProcessing && <Loader2 size={16} className="animate-spin" color="#0066FF" />}
                {isProcessing ? 'Trwa synchronizacja stopek z Gmail API...' : 'Synchronizacja zakończona!'}
              </span>
              <span style={{ color: '#0066FF', fontWeight: 700 }}>{Math.round(progress)}%</span>
            </div>

            {/* Progress Bar */}
            <div
              style={{
                width: '100%',
                height: 8,
                background: '#e2e8f0',
                borderRadius: 9999,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #0066FF, #38bdf8)',
                  transition: 'width 0.3s ease-out',
                  borderRadius: 9999,
                }}
              />
            </div>
          </div>

          {/* User Status Items */}
          <div
            style={{
              maxHeight: 260,
              overflowY: 'auto',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              background: '#f8fafc',
              padding: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {selectedUsers.map((user) => {
              const itemResult = result?.results.find((r) => r.email === user.primaryEmail);
              const isDone = Boolean(itemResult);
              const isSuccess = itemResult?.success;

              return (
                <div
                  key={user.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: '#ffffff',
                    borderRadius: 6,
                    border: '1px solid #e2e8f0',
                    fontSize: 12,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontWeight: 600, color: '#0B192C' }}>{user.fullName}</div>
                    <div style={{ color: '#64748b' }}>({user.primaryEmail})</div>
                  </div>

                  <div>
                    {!isDone ? (
                      <span style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Loader2 size={13} className="animate-spin" /> W kolejce...
                      </span>
                    ) : isSuccess ? (
                      <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
                        <CheckCircle2 size={14} /> Wstrzyknięto
                      </span>
                    ) : (
                      <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600 }}>
                        <AlertCircle size={14} /> Błąd
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Result Summary */}
          {result && (
            <div
              style={{
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                borderRadius: 8,
                padding: '12px 16px',
                fontSize: 13,
                color: '#0B192C',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <strong>Podsumowanie operacji:</strong> Zaktualizowano pomyślnie{' '}
                <span style={{ color: '#059669', fontWeight: 700 }}>{result.successful}</span> z{' '}
                <span>{result.total}</span> kont.
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  ID Audytu Firestore: <code>{result.batchId}</code>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            background: '#f8fafc',
          }}
        >
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="btn btn-primary"
            style={{ padding: '8px 24px' }}
          >
            {isProcessing ? 'Przetwarzanie w toku...' : 'Zamknij i powróć do panelu'}
          </button>
        </div>
      </div>
    </div>
  );
};
